import { apiClient } from './apiClient';

// Simuliere API-Verzögerung
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

class ScheduleService {
  constructor() {
    this.schedule = [];
  }

  // Alle Zeitplan-Events abrufen
  async getAllScheduleEvents(filters = {}) {
    try {
      await delay(300);

      // Query-Parameter aufbauen
      const queryParams = new URLSearchParams();
      if (filters.date) queryParams.append('date', filters.date);
      if (filters.venue) queryParams.append('venue', filters.venue);
      if (filters.status) queryParams.append('status', filters.status);

      const endpoint = `/schedule${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(endpoint);

      if (response) {
        return {
          success: true,
          data: response,
          message: 'Zeitplan erfolgreich geladen'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Laden des Zeitplans'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Laden des Zeitplans',
        details: error.message
      };
    }
  }

  // Zeitplan nach Datum gruppiert abrufen
  async getScheduleByDate() {
    try {
      await delay(300);

      const response = await apiClient.get('/schedule/by-date');

      if (response) {
        return {
          success: true,
          data: response,
          message: 'Zeitplan nach Datum erfolgreich geladen'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Laden des Zeitplans'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Laden des Zeitplans',
        details: error.message
      };
    }
  }

  // Prüfen ob bereits Events existieren
  async hasExistingEvents() {
    try {
      await delay(200);

      const response = await apiClient.get('/schedule/has-events');

      if (response) {
        return {
          success: true,
          data: response,
          message: 'Status abgerufen'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Prüfen der Events'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Prüfen der Events',
        details: error.message
      };
    }
  }

  // Zeitplan automatisch generieren
  async generateSchedule(options = {}) {
    try {
      await delay(1000);

      const response = await apiClient.post('/schedule/generate', options);

      if (response) {
        return {
          success: true,
          message: 'Zeitplan erfolgreich generiert',
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Generieren des Zeitplans'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Generieren des Zeitplans',
        details: error.message
      };
    }
  }

  // Zeiten im bestehenden Zeitplan aktualisieren
  async updateScheduleTimes() {
    try {
      await delay(800);

      const response = await apiClient.put('/schedule/update-times', {});

      if (response) {
        return {
          success: true,
          message: 'Zeiten im Zeitplan erfolgreich aktualisiert',
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Aktualisieren der Zeiten'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Aktualisieren der Zeiten',
        details: error.message
      };
    }
  }

  // Event im Zeitplan aktualisieren
  async updateScheduleEvent(eventId, updates) {
    try {
      await delay(500);

      const response = await apiClient.put(`/schedule/events/${eventId}`, updates);

      if (response) {
        return {
          success: true,
          message: 'Event erfolgreich aktualisiert',
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Aktualisieren des Events'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Aktualisieren des Events',
        details: error.message
      };
    }
  }

  // Event im Zeitplan verschieben (Drag & Drop)
  async moveScheduleEvent(eventId, newVenue, newSequenceNumber) {
    try {
      await delay(500);

      const response = await apiClient.put('/schedule/move', { eventId, newVenue, newSequenceNumber });

      if (response) {
        return {
          success: true,
          message: 'Event erfolgreich verschoben',
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Verschieben des Events'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Verschieben des Events',
        details: error.message
      };
    }
  }

  // Zeitplan zurücksetzen
  async resetSchedule() {
    try {
      await delay(500);

      const response = await apiClient.delete('/schedule/reset');

      if (response) {
        return {
          success: true,
          message: 'Zeitplan erfolgreich zurückgesetzt'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Zurücksetzen des Zeitplans'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Zurücksetzen des Zeitplans',
        details: error.message
      };
    }
  }

  // Konflikte prüfen
  async checkConflicts() {
    try {
      await delay(300);

      const response = await apiClient.get('/schedule/conflicts');

      if (response) {
        return {
          success: true,
          data: response,
          message: 'Konflikte erfolgreich geprüft'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Prüfen der Konflikte'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Prüfen der Konflikte',
        details: error.message
      };
    }
  }

  // Mock-Daten für Fallback
  async getMockSchedule() {
    await delay(300);

    return {
      success: true,
      data: [
        {
          id: 1,
          group: {
            id: 1,
            name: "Tan Tui - Jugend - Männer",
            category: { name: "Tan Tui", type: "Handform" },
            ageGroup: { name: "Jugend" },
            gender: "männlich"
          },
          venue: "Halle 1",
          startTime: "2026-07-15T09:00:00.000Z",
          endTime: "2026-07-15T09:45:00.000Z",
          sequenceNumber: 1,
          status: "Ausstehend"
        },
        {
          id: 2,
          group: {
            id: 2,
            name: "Ba Gua Dao - Erwachsene - Frauen",
            category: { name: "Ba Gua Dao", type: "Waffenform" },
            ageGroup: { name: "Erwachsene" },
            gender: "weiblich"
          },
          venue: "Halle 2",
          startTime: "2026-07-15T09:00:00.000Z",
          endTime: "2026-07-15T09:35:00.000Z",
          sequenceNumber: 1,
          status: "Ausstehend"
        }
      ],
      message: 'Mock-Zeitplan geladen'
    };
  }

  // Fallback-Methode: Versuche echte API, falls fehlschlägt Mock-Daten
  async getAllScheduleEventsWithFallback(filters = {}) {
    try {
      return await this.getAllScheduleEvents(filters);
    } catch (error) {
      console.warn('API nicht verfügbar, verwende Mock-Daten:', error);
      return await this.getMockSchedule();
    }
  }

  // Fallback-Methode für Zeitplan nach Datum
  async getScheduleByDateWithFallback() {
    try {
      return await this.getScheduleByDate();
    } catch (error) {
      console.warn('API nicht verfügbar, verwende Mock-Daten:', error);
      return await this.getMockSchedule();
    }
  }

  // Alle Änderungen im Zeitplan speichern
  async saveScheduleChanges(changes) {
    try {
      await delay(1000);

      const response = await apiClient.post('/schedule/save-changes', changes);

      if (response) {
        return {
          success: true,
          message: 'Änderungen erfolgreich gespeichert',
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Speichern der Änderungen'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Speichern der Änderungen',
        details: error.message
      };
    }
  }

  // Mehrere Events auf einmal aktualisieren
  async updateMultipleEvents(events) {
    try {
      await delay(800);

      const response = await apiClient.put('/schedule/update-multiple', { events });

      if (response) {
        return {
          success: true,
          message: 'Events erfolgreich aktualisiert',
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Aktualisieren der Events'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Aktualisieren der Events',
        details: error.message
      };
    }
  }

  // Turnier starten
  async startTournament() {
    try {
      await delay(1000);

      const response = await apiClient.post('/schedule/start-tournament');

      if (response) {
        return {
          success: true,
          message: 'Turnier erfolgreich gestartet',
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Starten des Turniers'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Starten des Turniers',
        details: error.message
      };
    }
  }

  // Block-Vorschau abrufen (Debug)
  async getBlockPreview() {
    try {
      const response = await apiClient.get('/schedule/block-preview');

      if (response) {
        return {
          success: true,
          data: response.data || response,
          message: 'Block-Vorschau erfolgreich geladen'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Laden der Block-Vorschau'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Laden der Block-Vorschau',
        details: error.message
      };
    }
  }
  // Block-Reihenfolge speichern
  async saveBlockOrder(blocks) {
    try {
      const response = await apiClient.post('/schedule/save-block-order', { blocks });

      if (response) {
        return {
          success: true,
          message: 'Block-Reihenfolge erfolgreich gespeichert',
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Speichern der Block-Reihenfolge'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Speichern der Block-Reihenfolge',
        details: error.message
      };
    }
  }
}

export default new ScheduleService(); 